/**
 * Image format support detection utility
 *
 * Detects browser support for AVIF and WebP formats.
 * Results are cached after first detection to avoid repeated checks.
 */

let avifSupported: boolean | null = null;
let webpSupported: boolean | null = null;
let detectionPromise: Promise<{ avif: boolean; webp: boolean }> | null = null;
let detectionInitiated = false;

/**
 * Initialize format detection after initial page load
 * Deferred to avoid blocking initial render and hero image loading
 */
if (typeof window !== "undefined") {
  // Store window reference with proper typing
  const win = window as Window & {
    requestIdleCallback?: (
      callback: () => void,
      options?: { timeout?: number }
    ) => number;
    cancelIdleCallback?: (id: number) => void;
  };

  // Defer detection until after initial load to prioritize hero images
  // Detection will start when browser is idle or after a delay
  const initDetection = () => {
    if (!detectionInitiated) {
      detectionInitiated = true;
      detectImageFormatSupport().catch(() => {
        // Silently fail - if detection fails, we'll default to PNG-first
      });
    }
  };

  // Use requestIdleCallback if available, otherwise defer after load
  if (win.requestIdleCallback) {
    win.requestIdleCallback(
      () => {
        initDetection();
      },
      { timeout: 2000 }
    );
  } else if (document.readyState === "complete") {
    // Page already loaded, defer with short delay
    setTimeout(initDetection, 100);
  } else {
    // Wait for page load, then defer
    win.addEventListener(
      "load",
      () => {
        setTimeout(initDetection, 100);
      },
      { once: true }
    );
  }
}

/**
 * Check if browser supports AVIF format
 * Uses feature detection by attempting to decode a minimal AVIF image
 */
function checkAVIFSupport(): Promise<boolean> {
  return new Promise((resolve) => {
    if (avifSupported !== null) {
      resolve(avifSupported);
      return;
    }

    const img = new Image();
    img.onload = () => {
      avifSupported = true;
      resolve(true);
    };
    img.onerror = () => {
      avifSupported = false;
      resolve(false);
    };
    // Minimal 1x1 transparent AVIF image (base64)
    img.src =
      "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A=";
  });
}

/**
 * Check if browser supports WebP format
 * Uses feature detection by attempting to decode a minimal WebP image
 */
function checkWebPSupport(): Promise<boolean> {
  return new Promise((resolve) => {
    if (webpSupported !== null) {
      resolve(webpSupported);
      return;
    }

    const img = new Image();
    img.onload = () => {
      webpSupported = true;
      resolve(true);
    };
    img.onerror = () => {
      webpSupported = false;
      resolve(false);
    };
    // Minimal 1x1 transparent WebP image (base64)
    img.src =
      "data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA";
  });
}

/**
 * Detect image format support
 * Returns a promise that resolves with support information
 * Results are cached after first detection
 *
 * If called before deferred initialization, will trigger detection immediately
 */
export function detectImageFormatSupport(): Promise<{ avif: boolean; webp: boolean }> {
  // Return cached promise if detection is already in progress
  if (detectionPromise) {
    return detectionPromise;
  }

  // Return cached results if available
  if (avifSupported !== null && webpSupported !== null) {
    return Promise.resolve({
      avif: avifSupported,
      webp: webpSupported,
    });
  }

  // Mark as initiated if called explicitly
  detectionInitiated = true;

  // Start detection
  detectionPromise = Promise.all([checkAVIFSupport(), checkWebPSupport()]).then(
    ([avif, webp]) => {
      detectionPromise = null; // Clear promise after completion
      return { avif, webp };
    }
  );

  return detectionPromise;
}

/**
 * Get cached format support (synchronous)
 * Returns null if detection hasn't completed yet
 */
export function getCachedFormatSupport(): { avif: boolean; webp: boolean } | null {
  if (avifSupported === null || webpSupported === null) {
    return null;
  }
  return {
    avif: avifSupported,
    webp: webpSupported,
  };
}

/**
 * Reset cached format support (useful for testing)
 */
export function resetFormatSupportCache(): void {
  avifSupported = null;
  webpSupported = null;
  detectionPromise = null;
}
